import Reveal from "./Reveal";

/**
 * The PWT logo is a "daisy" / Ikigai: each petal represents a partner,
 * forming a single, cohesive shape — a radial form orbiting a common centre.
 */
function DaisyMark() {
  const petals = 8;
  return (
    <div className="relative w-full aspect-square max-w-[520px] mx-auto" data-testid="daisy-visual">
      <svg
        viewBox="-220 -220 440 440"
        className="w-full h-full daisy-rotate"
        aria-hidden="true"
      >
        {Array.from({ length: petals }).map((_, i) => {
          const angle = (360 / petals) * i;
          return (
            <g key={i} transform={`rotate(${angle})`}>
              <ellipse
                cx="0"
                cy="-110"
                rx="40"
                ry="95"
                fill="#D8E2DC"
                opacity="0.7"
              />
              <ellipse
                cx="0"
                cy="-110"
                rx="40"
                ry="95"
                fill="none"
                stroke="#1A402B"
                strokeWidth="0.6"
                opacity="0.55"
              />
            </g>
          );
        })}
        <circle cx="0" cy="0" r="34" fill="#1A402B" />
        <circle cx="0" cy="0" r="34" fill="none" stroke="#0A140F" strokeWidth="0.8" />
        <text
          x="0"
          y="6"
          textAnchor="middle"
          fontFamily="'Cormorant Garamond', serif"
          fontSize="20"
          fontStyle="italic"
          fill="#FFFFFF"
        >
          PWT
        </text>
      </svg>
    </div>
  );
}

export default function Ecosystem() {
  return (
    <section
      id="ecosystem"
      data-testid="ecosystem-section"
      className="relative py-24 md:py-32 bg-[#F8F9F8]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-10 md:gap-16 items-center">
          <Reveal className="col-span-12 md:col-span-6 order-2 md:order-1">
            <span className="overline">04 — The origin of our symbol</span>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-[#0A140F] leading-[1.05]">
              The PWT logo encodes the philosophy of the ecosystem in its very shape.
            </h2>

            <div className="mt-10 space-y-8">
              <div>
                <div className="overline text-[#1A402B]">The Daisy</div>
                <p className="mt-3 font-sans text-base md:text-lg leading-relaxed text-[#415248]">
                  Each petal represents a partner with their specific expertise.
                  Together they form a single, cohesive shape — just as the
                  ecosystem&apos;s boutique firms, each excellent in their domain,
                  combine into one integrated offering.
                </p>
                <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-[#0A140F]">
                  Petals = Partners and Specific Expertise · Centre = Point of Synthesis and Coordination
                </p>
              </div>

              <div>
                <div className="overline text-[#1A402B]">The Radial Form</div>
                <p className="mt-3 font-sans text-base md:text-lg leading-relaxed text-[#415248]">
                  Inspired by the Ikigai — the Japanese concept of purpose and reason
                  for being — the radial symmetry represents coordinated synergy and
                  shared value. Every partner orbits a common centre, aligned toward
                  a single purpose.
                </p>
                <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-[#0A140F]">
                  Radial Form = Coordinated Synergy and Shared Value
                </p>
              </div>

              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#0A140F] border-t border-[#E5EBE6] pt-6">
                Ikigai (purpose) &nbsp;+&nbsp; Daisy (partners) &nbsp;=&nbsp; PWT (purpose meets coordinated expertise)
              </div>
            </div>
          </Reveal>

          <Reveal className="col-span-12 md:col-span-6 order-1 md:order-2" delay={120}>
            <DaisyMark />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
